import React, { useEffect, useRef, useState } from 'react';
import { observer } from 'mobx-react';
import { Accordion } from 'cd-eft/components/common/Accordion';
import { ButtonBlock } from '../../common/ButtonBlock/ButtonBlock';
import { TerraceDesignProfileViewVM } from './TerraceDesignProfileViewVM';
import { ReachTable, ReachTableTabPane } from '../../common/ReachTable/ReachTable';
import stores from 'cd-eft/models/Stores';
import { reachTableTabs } from './TerraceDesignProfileView.config';
import { ActionsMenu } from '../../common/ActionsMenu';
import { DeleteElement } from 'cd-eft/components/Design/common/DeleteElement';
import './TerraceDesignProfileView.scss';
import { DynamicTerraceHub } from 'cd-eft/models/Stores/DynamicProfile/Terrace/DynamicTerraceHub';
import { DynamicProfileStoreBase } from 'cd-eft/models/Stores/DynamicProfile/DynamicProfileStoreBase';
import { DynamicTableStoreBase } from 'cd-eft/models/Stores/DynamicProfile/DynamicTableStoreBase';
import { ProfileStoreType } from 'cd-eft/models/Stores/DynamicProfile/DynamicProfileEnums';
import { EngineeringMapService } from 'cd-eft/framework/mapServices/EngineeringMapServices';
import { DynamicTerraceProfile } from 'cd-eft/components/common/DynamicProfile/DynamicTerraceProfile';
import Draggable from 'react-draggable';
import { ProfileSettingsContainer } from '../../common/ProfileSettingsContainer/ProfileSettingsContainer';
import { Popup } from 'cd-eft/components/common/Popup';
import { mdiDelete, mdiHelpCircleOutline, mdiMagnify } from '@mdi/js';
import { Modal, ModalHeader, ModalBody, ModalFooter, Input } from 'reactstrap';
import { UserLeavingWarning } from 'cd-eft/components/common/UserLeavingWarning/UserLeavingWarning';
import { ResultsCard } from '../../common/ResultsCard/ResultsCard';
import { ReachTemplateContainer } from '../../common/ReachTemplate/ReachTemplate';
import Icon from '@mdi/react';
import { StaticTooltip } from 'cd-eft/components/common/StaticTooltip/StaticTooltip';
import { useHistory } from 'react-router-dom';
import { PrintComponent } from 'cd-eft/components/common/PrintPDF';
import { PrintDesignReports } from 'cd-eft/components/Projects/PrintDesignReports';
import { handleActionValueInputChange, structureTypes } from 'cd-eft/models/Stores/DesignStore';
import { OnClickReachInformation } from '../../common/OnClickReachInformation/OnClickReachInformation';

export const BlockModal = observer(({ vm }: { vm: TerraceDesignProfileViewVM }) => {
  const stationName = vm.selectedStation[0]?.reachId === 'START' || vm.selectedStation[0]?.reachId === 'END' 
    ? '' 
    : vm.selectedStation[0]?.endStation;
  const hasBlock = vm.selectedStation[0]?.blockStationDto?.blockHeight;
  const headerText = vm.selectedStation[0]
    ? `${hasBlock ? 'Edit' : 'Add'} Storage Block ${stationName}`
    : 'Add Storage Block';

  return (
    <Modal 
      className='terrace-modal-container'
      isOpen={vm.showBlockModal}
      toggle={vm.handleCloseBlock}
      centered={true}
      fade={false}
    >
      <Draggable axis="both" positionOffset={{ x: '-50%', y: '-50%' }} cancel="input,select">
        <div className="block-modal">
          <ModalHeader>
            <div className="header-content">{headerText}</div>
          </ModalHeader>
          <ModalBody>
            <div className="terrace-modal-content">
              <div className="modal-row">
                <div>
                  <label htmlFor="top_width">Top Width (ft)</label>
                  <Input
                    id="top_width"
                    name="top_width"
                    type="number"
                  value={vm.blockModalState.topWidth ?? ''}
                    step="0.01"
                    min="0"
                    onChange={(e) => vm.handleBlockModalChange({topWidth: e.target.value === '' ? undefined : Number(e.target.value)})}
                    onBlur={(e) => vm.handleBlockInputBlur('topWidth', e.target.value === '' ? undefined : Number(e.target.value))}
                    className={`usa-input ${vm.blockValidation.formErrors.topWidth ? 'usa-input--error' : ''}`}
                  />
                </div>
                <div>
                  <label htmlFor="block_height">Block Height</label>
                  <select 
                    id="block_height" 
                    name="block_height" 
                    className="usa-select"
                    value={vm.blockModalState.blockHeight || ''}
                    onChange={(e) => vm.handleBlockModalChange({ blockHeight: e.target.value })}
                  >
                    <option value="FULL_HEIGHT">Full Height</option>
                    <option value="Half_Height" disabled={vm.blockModalState.isSplitSubbasin}>Half Height</option>
                    <option value="Three_Quarter" disabled={vm.blockModalState.isSplitSubbasin}>Three Quarter</option>
                    <option value="Water_Level_Freeboard">Water Level + Freeboard</option>
                  </select>
                </div>
              </div>

              <div className="modal-row block-checkbox-row">
                <div className="block-checkbox">
                  <input
                    type="checkbox"
                    id="split_subbasin"
                    name="split_subbasin"
                    className="usa-checkbox"
                    checked={vm.blockModalState.isSplitSubbasin}
                    onChange={(e) => vm.handleBlockModalChange({ isSplitSubbasin: e.target.checked })}
                  />
                  <label htmlFor="split_subbasin">Split Subbasin</label>
                </div>
                <button className="usa-button" onClick={vm.handleEditBlock}>
                  Rotate Block
                </button>
              </div>

              <div>
                <h4 className="block-slopes-title">Block Side Slope</h4>
                <div className="modal-row">
                  <div>
                    <label htmlFor="slope_mode">Mode</label>
                    <select 
                      id="slope_mode" 
                      name="slope_mode" 
                      className="usa-select"
                      value={vm.blockModalState.sideSlopeMode}
                      onChange={(e) => vm.handleBlockModalChange({ sideSlopeMode: Number(e.target.value) })}
                    >
                      <option value={1}>Ratio-Based</option>
                      <option value={2}>Width-Based</option>
                    </select>
                  </div>
                  <div>
                    <label htmlFor="slope_value">Value (ft/ft)</label>
                    <Input
                      id="slope_value"
                      name="slope_value"
                      type="number"
                      value={vm.blockModalState.sideSlope ?? ''}
                      step="0.01"
                      min="0"
                      onChange={(e) => vm.handleBlockModalChange({sideSlope: e.target.value === '' ? undefined : Number(e.target.value)})}
                      onBlur={(e) => vm.handleBlockInputBlur('sideSlope', e.target.value === '' ? undefined : Number(e.target.value))}
                      className={`usa-input ${vm.blockValidation.formErrors.sideSlope ? 'usa-input--error' : ''}`}
                    />
                  </div>
                  <div>
                    <label htmlFor="slope_limit">Limit (ft)</label>
                    <Input
                      id="slope_limit"
                      name="slope_limit"
                      type="number"
                      value={vm.blockModalState.limits ?? ''}
                      step="0.01"
                      min="0"
                      onChange={(e) => vm.handleBlockModalChange({limits: e.target.value === '' ? undefined : Number(e.target.value)})}
                      onBlur={(e) => vm.handleBlockInputBlur('limits', e.target.value === '' ? undefined : Number(e.target.value) )}
                      className={`usa-input ${vm.blockValidation.formErrors.limits ? 'usa-input--error' : ''}`}
                    />
                  </div>
                </div>
              </div>
            </div>
          </ModalBody>
          <ModalFooter>
            <div className="modal-buttons">
              <button className="usa-button usa-button--outline close-button" onClick={vm.handleCloseBlock}>
                Close
              </button>
              <button className="usa-button save-button" onClick={vm.handleApplyBlock} disabled={vm.blockValidation.hasError || vm.blockModalState.topWidth <= 0 ||vm.blockModalState.sideSlope <= 0 || vm.blockModalState.limits <= 0}>              
                Apply
              </button>
            </div>
          </ModalFooter>
        </div>
      </Draggable>
    </Modal>
  );
});

export const DrainStationModal = observer(({ vm }: { vm: TerraceDesignProfileViewVM }) => {
  const stationName = vm.selectedStation[0]?.endStation;
  const hasDrain = vm.selectedStation[0]?.drainStation;
  const headerText = vm.selectedStation[0] 
    ? `${hasDrain ? 'Edit' : 'Add'} Drain Station ${stationName}`
    : 'Add Drain';
    
  return (
    <Modal 
      className='terrace-modal-container'
      isOpen={vm.showDrainModal}
      toggle={vm.handleCloseDrain}
      centered={true}
      fade={false}
    >
      <Draggable axis="both" positionOffset={{ x: '-50%', y: '-50%' }} cancel="input,select">
        <div className="drain-modal">
          <ModalHeader>
            <div className="header-content">{headerText}</div>
          </ModalHeader>
          <ModalBody>
            <div className="terrace-modal-content">
              <div className="modal-row">
                <div>
                  <label htmlFor="drain_name">Drain Name</label>
                  <Input
                    id="drain_name"
                    name="drain_name"
                    type="text"
                    value={vm.drainModalState.drainName ?? ''}
                    onChange={(e) => vm.handleDrainModalChange({ drainName: e.target.value === '' ? undefined : e.target.value})}
                    onBlur={(e) => vm.handleDrainInputBlur('drainName', e.target.value === '' ? undefined : e.target.value)}
                    className={`usa-input ${vm.drainValidation.invalidFields.some(
                      field => field.fieldName === 'drainName' && field.index === 0
                    ) ? 'usa-input--error' : ''}`}
                  />
                </div>
                <div>
                  <label htmlFor="drain_fraction">Drain Fraction (%)</label>
                  <Input
                    id="drain_fraction"
                    name="drain_fraction"
                    type="number"
                    value={vm.drainModalState.drainFraction ?? ''}
                    onChange={(e) => vm.handleDrainModalChange({ drainFraction: e.target.value === '' ? undefined : Number(e.target.value)})}
                    onBlur={(e) => vm.handleDrainInputBlur('drainFraction', e.target.value === '' ? undefined : e.target.value)}
                    min="0"
                    max="100"
                    className={`usa-input ${vm.drainValidation.invalidFields.some(
                      field => field.fieldName === 'drainFraction' && field.index === 0
                    ) ? 'usa-input--error' : ''}`}
                  />
                </div>
                <div>
                  <label htmlFor="drain_offset">Drain Offset (ft)</label>
                  <Input
                    id="drain_offset"
                    name="drain_offset"
                    type="number"
                    value={vm.drainModalState.drainOffset ?? ''}
                    onChange={(e) => vm.handleDrainModalChange({ drainOffset: e.target.value === '' ? undefined : Number(e.target.value)})}
                    onBlur={(e) => vm.handleDrainInputBlur('drainOffset', e.target.value === '' ? undefined : e.target.value)}
                    step="0.01"
                    min="0"
                    className={`usa-input ${vm.drainValidation.invalidFields.some(
                      field => field.fieldName === 'drainOffset' && field.index === 0
                    ) ? 'usa-input--error' : ''}`}
                  />
                </div>
              </div>
              {vm.existingDrainStations.length > 0 && (
                <div className="drain-table">
                  <table className="usa-table usa-table--borderless mv-table">
                    <thead>
                      <tr>
                        <th className="narrow-td">Station Name</th>
                        <th className="narrow-td">Drain Name</th>
                        <th className="narrow-td">Drain Fraction (%)</th>
                        <th className="narrow-td">Drain Offset (ft)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {vm.existingDrainStations.length > 0 && vm.existingDrainStations.map((rowData, i) => (
                        <tr key={`drain_row-${i}`}>
                          <td >{rowData.stationName}</td>
                          <td>
                            <Input
                              id={`drain_name-${i}`}
                              name={`drain_name-${i}`}
                              type="text"
                              value={rowData.drainStationDto.drainName ?? ''}
                              onChange={(e) => vm.handleExistingDrainChange('drainName', e.target.value === '' ? undefined : e.target.value, i)}
                              onBlur={(e) => vm.handleDrainInputBlur('drainName', e.target.value === '' ? undefined : e.target.value, i)}
                              className={`usa-input ${vm.drainValidation.invalidFields.some(
                                field => field.fieldName === 'drainName' && field.index === i + 1
                              ) ? 'usa-input--error' : ''}`}
                            />
                          </td>
                          <td>
                            <Input
                              id={`drain_fraction-${i}`}
                              name={`drain_fraction-${i}`}
                              type="number"
                              value={rowData.drainStationDto.drainFraction ?? ''}
                              onChange={(e) => vm.handleExistingDrainChange('drainFraction', e.target.value === '' ? undefined : Number(e.target.value), i)}
                              onBlur={(e) => vm.handleDrainInputBlur('drainFraction', e.target.value === '' ? undefined : e.target.value, i)}
                              min="0"
                              max="100"
                              className={`usa-input ${vm.drainValidation.invalidFields.some(
                                field => field.fieldName === 'drainFraction' && field.index === i + 1
                              ) ? 'usa-input--error' : ''}`}
                            />
                          </td>
                          <td>
                            <Input
                              id={`drain_offset-${i}`}
                              name={`drain_offset-${i}`}
                              type="number"
                              value={rowData.drainStationDto.drainOffset ?? ''}
                              onChange={(e) => vm.handleExistingDrainChange('drainOffset', e.target.value === '' ? undefined : Number(e.target.value), i)}
                              onBlur={(e) => vm.handleDrainInputBlur('drainOffset', e.target.value === '' ? undefined : e.target.value, i)}
                              step="0.01"
                              min="0"
                              className={`usa-input ${vm.drainValidation.invalidFields.some(
                                field => field.fieldName === 'drainOffset' && field.index === i + 1
                              ) ? 'usa-input--error' : ''}`}
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </ModalBody>
          <ModalFooter>
            <div className="modal-buttons">
              <p>Total Drain Fraction = {vm.totalDrainFraction}%</p>
              <div>
                <button className="usa-button usa-button--outline close-button" onClick={vm.handleCloseDrain}>
                  Close
                </button>
                <button className="usa-button save-button" onClick={vm.handleApplyDrain} disabled={vm.drainValidation.hasError || vm.drainModalState.drainFraction <= 0 || vm.drainModalState.drainName?.trim() === ''}>    
                  Apply
                </button>
              </div>
            </div>
          </ModalFooter>
        </div>
      </Draggable>
    </Modal>
  );
});

export const EditReachTemplateModal = observer(({ vm }: { vm: TerraceDesignProfileViewVM }) => {
  return (
    <Draggable axis="both" positionOffset={{ x: '-50%', y: '-50%' }} cancel="input,select">
      <div className="template-modal">
        <ModalHeader>
          <div className="template-header">
            Edit Reach Template: {vm.terraceTemplateVM.reachTemplateForm.templateName}
          </div>
        </ModalHeader>
        <ModalBody>
          <ReachTemplateContainer
            reachTemplateForm={vm.terraceTemplateVM.reachTemplateForm}
            formValidation={vm.terraceTemplateVM.formValidation}
            handleInputChange={vm.handleReachTemplateInputChange} 
          />
        </ModalBody>
        <ModalFooter>
          <div className="template-buttons">
            <button className="usa-button usa-button--outline" onClick={vm.handleCancelReachTemplate}>
              Close
            </button>
            <button className="usa-button"
              onClick={vm.handleSaveReachTemplate}
              disabled={terraceDesignStore.terraceInputsChanged ? !vm.terraceTemplateVM.terraceTemplateChanged : true}>
              Apply Properties to Selected Reaches
            </button>
          </div>
        </ModalFooter>
      </div>
    </Draggable>
  );
});

const { terraceDesignStore, designStore } = stores.AppStore;

const TerraceDesignProfileViewComponent = (props) => {
  const history = useHistory();

  let profileHub: DynamicTerraceHub;
  const profileStoreRef = useRef<DynamicProfileStoreBase>();
  const profileTableStoreRef = useRef<DynamicTableStoreBase>();

  if (!profileTableStoreRef.current && !profileStoreRef.current) {
    profileHub = new DynamicTerraceHub();
    profileHub.createStore(ProfileStoreType.MAP, { mapView: EngineeringMapService.instance?.mapView });
    profileHub.createStore(ProfileStoreType.PROFILE);
    profileStoreRef.current = profileHub.ProfileStore;
    profileHub.createStore(ProfileStoreType.TABLE);
    profileTableStoreRef.current = profileHub.TableStore;
  }

  const vmREF = useRef<TerraceDesignProfileViewVM>();
  if(vmREF.current?.history)  vmREF.current.history = history
    if (!vmREF.current) {
        vmREF.current = new TerraceDesignProfileViewVM(
            {
                ...props,
                tableStoreRef: profileTableStoreRef.current,
                profileStoreRef: profileStoreRef.current
            },
        );
    }

    const vm = vmREF.current;

    useEffect(() => {
      vm.onMount();
      return () => {
        vm.unMount();
        vm.removeReachLabelLayers();
        terraceDesignStore.checkedRowStations = [];
      };
    }, []);

  // ************** Profile Setup ************** //
  // Need to check if UI is fully rendered before drawing profile
  const [uiReady, setUiReady] = useState(false);
  const [uiMount, setUiMount] = useState(true);
  const profileContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const checkDimensions = () => {
      if (profileContainerRef.current) {
        const { width, height } = profileContainerRef.current.getBoundingClientRect();

        if (width > 0 && height > 0 && (terraceDesignStore.summaryInfo.terraceNameExists || !terraceDesignStore.terraceDesignObject.alignedElementDesignParametersId)) {
          setUiReady(true);
          return;
        }
      }
  
      requestAnimationFrame(checkDimensions);
    };

    checkDimensions();
  }, []);
  // ************ End Profile Setup ************ //

  useEffect(() => {
    // NOTE: This useEffect is reserved for updating data when the user clicks "Clear All" or "Undo" in the Pending Edit Manager.
    const resetValues = async () => {
      if (designStore.toggleResetValues) {
        await profileStoreRef.current?.createReachFromSavedDesign(terraceDesignStore.terraceDesignObject.terraceDesignStations, terraceDesignStore.terraceDesignObject.alignedElementDesignParametersId);
        designStore.toggleResetValues = false;
      }
    };
    resetValues();
  }, [designStore.toggleResetValues]);

  useEffect(() => {
    const toggleSecondaryLayers = async () => {
      if(uiMount){
       setUiMount(false);
      }
      else{
        profileStoreRef.current?.toggleSecondaryLayersVisibility(designStore.setTerraceSecLayerVisibility);
      }
    };
    toggleSecondaryLayers();
  }, [designStore.setTerraceSecLayerVisibility]);

  useEffect(() => {
    if (terraceDesignStore.terraceReport.waterElevation) vm.setTerraceResultsComment();
  }, [terraceDesignStore.terraceReport.waterElevation])

  useEffect(() => {
    vm.closeMatchVertexModal();
  }, [terraceDesignStore.closeMatchModal])

  return (
    <>
      <div ref={profileContainerRef} id="separator-top-container">
        {terraceDesignStore.displayResultBanner && (
          <Accordion
            className="accordion-wrapper results-info"
            headers={['Results']}
            expanded={true}
            light={true}
            onToggle={vm.removeHeights}
          >
            <ResultsCard
              fields={vm.terraceResultsFields}
              result={terraceDesignStore.terraceReport}
              comment={vm.terraceResultsComment}
            >
              <button type="button" className="usa-button px-3" onClick={vm.toggleReport}>
                Design Report
              </button>
            </ResultsCard>
          </Accordion>
        )}
        {
            <Modal className="project-owner-modal" isOpen={vm.saveButtonPopup}>
                <ModalBody>
                    <PrintComponent
                        componentToPrint={PrintDesignReports}
                        printButtonText={'Report'}
                        functionProp={vm}
                        printButtonClasses={'usa-button usa-button--outline'}
                        //componentProps is used to pass the calculation results to the pdf
                        componentProps={{reportData: vm.terraceDesignReportObj,project: designStore.projectsStore.currentProject  }}
                        callFrom='WDTReport' //same as wdtReport
                    />
                </ModalBody>
            </Modal>
        }

        {/* *****Profile****** */}
        <Accordion
          key={`terrace-profile-tab-content-profile-view`}
          className="accordion-wrapper profile-info"
          headers={['Profile View']}
          expanded={true}
          light={true}
          iconPathQ={true}
          onClickInformationToggle={vm.onClickProfileInformationToggle}
          actionButton={terraceDesignStore.uiState ? 
          <ActionsMenu item="item" elementActions={vm.profileViewActions} hideIcon={true} disabled={false}>
            Action
          </ActionsMenu> : null
          }
          onToggle={vm.removeHeights}
        >
          <>
            <DynamicTerraceProfile profileStore={profileStoreRef.current} editable={false} generateInitialProfiles={vm.generateInitialProfiles} uiReady={uiReady} />
            <div id = "terrace-design-profile-btns">
              {terraceDesignStore.uiState ? <ButtonBlock buttons={vm.profileButtons} /> : null}
            </div>
          </>
        </Accordion>
      </div>

      <div id="separator"></div>

      <div id="separator-bottom-container">
        {/* *****Reach Table****** */}
        <Accordion
          className="accordion-wrapper profile-reaches-info pb-0"
          headers={['Terrace Profile/Reaches']}
          expanded={true}
          light={true}
          onToggle={vm.removeHeights}
        >
          <>
            <ReachTable tabs={reachTableTabs} activeTab={vm.activeTab} setTab={vm.setTab}>
              {reachTableTabs.map((el) => (
                <ReachTableTabPane key={`ReachTableTabPane-${el.tab}`} el={el} selectAll={vm.selectAll} activeTab={vm.activeTab} designType="terrace">
                   <tbody>
                    {terraceDesignStore.terraceDesignObject.terraceDesignStations.map((rowData, idx) => (
                      <tr
                        id={`row-terraceDesignStation-${idx}`}
                        key={`row-${idx}`}
                        onMouseEnter={(e) => vm.handleRowMouseEnter(e, idx)}
                        onMouseLeave={(e) => vm.handleRowMouseLeave(e, idx)}
                      >
                        {/* Select/Deselect Checkbox */}
                        <td className="narrow-td">
                          {!['R-0', 'START', 'END'].includes(rowData.reachId) && (
                             <input
                              type="checkbox"
                              aria-label={`select ${rowData.reachId}`}
                              checked={terraceDesignStore.checkedRowStations.includes(rowData.reachId)}
                              onChange={(e) => {
                                  vm.handleCheckboxChange(e, idx);
                              }}
                            />
                          )}
                        </td>
                         {/* Status */}
                         <td>
                          <div className='status-container'>
                            <div
                              className={rowData.status && !['R-0', 'START', 'END'].includes(rowData.reachId) ? `status-label status--${rowData.status.replace(" ", "_")}` : ""}
                              data-testid={`status-${idx}`}
                            >
                              {!['R-0', 'START', 'END'].includes(rowData.reachId) && rowData.status === 'WARNING_STATUS'
                                ? (<span> WARNING </span>)
                                : (<span> {['R-0', 'START', 'END'].includes(rowData.reachId)
                                  ? ""
                                  : rowData.status?.replace("_STATUS", "")}</span>)
                              }
                            </div>
                            {rowData.status && 
                              !['R-0', 'START', 'END'].includes(rowData.reachId) && 
                              ['ERROR', 'WARNING'].includes(rowData.status) && (
                                <div 
                                  className="question-button" 
                                  title=""
                                  onClick={() => vm.onClickInformationToggle(rowData.reachId)}
                                >
                                  <StaticTooltip 
                                    tooltipMessage={"More Information"} 
                                    tooltipSide="bottom" 
                                    id={`tooltip-${idx}`} 
                                    icon={mdiHelpCircleOutline} 
                                    title="" 
                                  />
                                </div>
                              )}
                          </div>
                        </td>
                        {/* Dynamic Columns based on el.defaultCols */}
                        {el.defaultCols.map((col, colIdx) => {
                          // const isCutSlopeField = col.name === 'templateParams.cutSlope';
                          // const isBottomWidthField = col.name === 'bottomWidth';
                          // const isCombinedView = vm.activeTab === 'combined-view';                        
                          // const isNonEditableField = (isBottomWidthField && !isCombinedView); // Make bottomWidth non-editable in template view
                          // const isEditable = (col.editable || ((isCutSlopeField || isBottomWidthField) && isCombinedView)) && !isNonEditableField;
                          
                          // let value;
                          // if (col.name.includes('.')) {
                          //   const [parentKey, childKey] = col.name.split('.');
                          //   value = rowData[parentKey]?.[childKey] ?? '';
                          // } else {
                          //   value = rowData[col.name];
                          // }

                          // if (col.name === 'draiBblock') {
                          //   if (rowData.blockStationDto?.blockHeight) {
                          //     value = rowData.blockStationDto.blockHeight.replace(/_/g, ' ').toLowerCase();
                          //   } else if (rowData.drainStationDto?.drainName != null) {
                          //     value = `${rowData.drainStationDto.drainFraction}%`;
                          //   } else {
                          //     value = '';
                          //   }
                          // }

                          // // Decimal format the value based on the column name
                          // if (['groundElev', 'cutFill', 'stationDist', 'designHeight'].includes(col.name)) {
                          //   value = value !== null && value !== undefined && value !== ''
                          //   ? parseFloat(value).toFixed(1)
                          //   : value;
                          // } else if (typeof value === 'number' && !isEditable) {
                          //   value = value.toFixed(2);
                          // } else if (value === null || value === undefined) {
                          //   value = '';
                          // }

                          const [isEditable, value] = vm.setValue(col, rowData);

                          return (
                            <td  key={`cell-${idx}-${colIdx}`} className={`${col.name === 'draiBblock' ? 'capitalize' : ''}`}>
                              {isEditable ? (
                                <input
                                className={`usa-input ${
                                  vm.formValidationReachTable.invalidFields.some(
                                    field => field.fieldName === col.name && field.index === idx
                                  )
                                    ? 'usa-input--error'
                                    : ''
                                }`}
                                type="text"
                                aria-label={col.name}
                                value={value}
                                onChange={(e) => vm.handleInputChange(e, idx, col.name)}
                                onBlur={(e) => vm.handleTableInputChange(e, idx, col.name)}
                              />
                              ) : (
                                col.name === 'templateParams.cutSlope' ?
                                (
                                  <div>
                                    {value}&nbsp;
                                    {`${rowData.templateParams.cutSlopeMode === 1 ? "ft/ft" : "ft"}`}
                                    </div>
                                )
                                : (
                                  col.name === 'templateParams.frontSlope' ?
                                    (
                                      <div>
                                        {value}&nbsp;
                                        {`${rowData.templateParams.frontSlopeMode === 1 ? "ft/ft" : "ft"}`}
                                      </div>
                                    )
                                    : (
                                      col.name === 'templateParams.backSlope' ?
                                        (
                                          <div>
                                            {value}&nbsp;
                                            {`${rowData.templateParams.backSlopeMode === 1 ? "ft/ft" : "ft"}`}
                                          </div>
                                        )
                                        : value
                                    )
                                )
                              )}
                            </td>
                          );
                        })}
                        {/* Action Column */}
                        {vm.activeTab !== 'reach-view' && (
                          <td className={`${el.tab}-action-menu narrow-td action-td`}>
                            <ActionsMenu
                              i={idx}
                              item={rowData}
                              elementActions={vm.setElementActions(vm.activeTab, rowData)}
                            />
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>               
               </ReachTableTabPane>
              ))}
            </ReachTable>
          </>
        </Accordion>
      </div>
      <div id="terrace-design-btns" className="pr-3 pb-2 border-top">
        <ButtonBlock buttons={vm.reachTableButtons} />
      </div>
      
      {vm.profileSettingsForm.profileSettingPopUpActive &&
        <Modal 
          className='terrace-modal-container'
          isOpen={vm.profileSettingsForm.profileSettingPopUpActive}
          toggle={vm.handleCancelProfileSettings}
          centered={true}
          fade={false}
        >
          <Draggable
              axis="both"
              positionOffset={{ x: '-50%', y: '-50%' }}
              cancel="input"
          >
            <div className='profile-settings'>
              <ProfileSettingsContainer
                profileSettingsForm={vm.profileSettingsForm}
                formValidation={vm.formValidation}
                toggleFineGrid={vm.toggleFineGrid}
                handleCancelProfileSettings={vm.handleCancelProfileSettings}
                handleSaveProfileSettings={vm.handleSaveProfileSettings}
                title={'Profile Settings'}
                saveButtonText={'Apply'}
                parentType='Terrace'
                showVerticalInterval={true}
              />
              </div>
          </Draggable>
        </Modal>
      }

      {vm.showBlockModal && <BlockModal vm={vm} />}

      {vm.showDrainModal && <DrainStationModal vm={vm} />}
      {vm.showEditReachTemplateModal && <EditReachTemplateModal vm={vm} />}

      {vm.showMatchVertexModal && <MatchVertexModal vm={vm} />}

      {vm.deleteStationDialogIsOpen && (
        <DeleteElement
          trigger={vm.deleteStationDialogIsOpen}
          setTrigger={vm.toggleDeleteStationDialog}
          element="terrace station"
          name={vm.terraceDesignStationToDelete.reachId}
          action={vm.deleteTerraceDesignStation}
        />
      )}

      {vm.redigitizeReachesDialogIsOpen && (
        <Popup
            trigger={vm.redigitizeReachesDialogIsOpen}
            setTrigger={vm.toggleRedigitizeReachesDialog}
            title={`Replace Channel Profile?`}
            icon={mdiDelete}
            handleCancel={vm.toggleRedigitizeReachesDialog}
            cancelText="Close"
            handleOK={vm.digitizeReaches}
            okText="Ok"
        >
           <p>{vm.notification.redigitizeReaches}</p>
        </Popup>
      )}

      {vm.shiftElevationDialogIsOpen && (
        <Popup
          trigger={vm.shiftElevationDialogIsOpen}
          setTrigger={vm.toggleShiftElevationDialog}
          title={'Shift Terrace Elevation'}
          handleCancel={vm.cancelShiftElevationDialog}
          cancelText="Close"
          handleOK={vm.checkShiftElevation}
          okText="Save"
        >
          <p>Enter vertical shift to be applied to selected station.</p>
          <span>{'Vertical Shift (ft)'}</span>
          <input
            className="input-action-value"
            name="shiftElevation"
            type="text"
            value={vm.shiftElevation}
            onChange={(e) => handleActionValueInputChange(e, vm)}
            onBlur={vm.handleActionValueInputBlur}
          />
        </Popup>
      )}

      <UserLeavingWarning
        when={designStore.terraceGroupsLength > 0}
        section="Terrace Design Profile View"
        onOK={vm.userLeavingWarningOnOk}
        okText="Return"
        // cancelText="Cancel"
        // onCancel={vm.userLeavingWarningOnCancel}
        text={vm.notification.userLeavingWarning}
      />

      {vm.onClickInformation ? (
        <OnClickReachInformation
          onClickInformation={vm.onClickInformation}
          onClickInformationToggle={vm.onClickInformationToggle}
          errorCode={vm.errorCode}
          selectedReachId={vm.selectedReachId}
          errorMessage={vm.errorMessage}
        />
      ) : null}
      
      {vm.onClickProfileInformation ? (
        <Modal
            isOpen={vm.onClickProfileInformation}
            toggle={vm.onClickProfileInformationToggle}
            centered={true}
            className="eft-action-modal"
            aria-label={vm.headerTitle}
        >
          <ModalHeader toggle={vm.onClickProfileInformationToggle} className='header-info-detail'>
            <div className="display-flex flex-align-center">
              <strong>{`Information Details on ${vm.headerTitle}`}</strong>
            </div>
            {/* <a className="href-design-detail105" href="/design/designdetails" target="_blank">
              <strong>Open Information in a New Window</strong>
            </a> */}
          </ModalHeader>
          <ModalBody>
            {/* Add profile information */}
          </ModalBody>
          <ModalFooter>
            <button className="usa-button usa-button--outline" onClick={vm.onClickProfileInformationToggle}>
              Close
            </button>
          </ModalFooter>
        </Modal>
    ) : null}
    </>
  );
};

export const TerraceDesignProfileView = observer(TerraceDesignProfileViewComponent);

export const MatchVertexModal = observer(({ vm }: { vm: TerraceDesignProfileViewVM }) => {
  return (
    <Modal 
      className='terrace-modal-container'
      isOpen={vm.showMatchVertexModal}
      toggle={vm.closeMatchVertexModal}
      centered={true}
      fade={false}
    >
      <Draggable axis="both" positionOffset={{ x: '-50%', y: '-50%' }} cancel="input,select">
        <div className='mv-modal'>
          <ModalHeader>
            <div className="header-content">{`Match Vertex ${vm.matchTargetStationName}`}</div>
          </ModalHeader>
          <div className="search-container">
            <p className="m-0 pr-1">Searching Distance (ft)</p>
            <input
              className={`usa-input search-input ${vm.matchSearchDistanceState.valid ? 'search-input-valid' : 'usa-input--error'}`}
              name="search_distance_text_value"
              data-testid="search_distance_text_value"
              type="number"
              aria-label="20"
              value={vm.matchSearchDistanceState.distance ?? ''}
              onChange={vm.handleDistanceInputChange}
              onBlur={vm.handleDistanceInputBlur}
              min="1"
            />
            <button className="search-icon usa-button" onClick={vm.callMatchAPI} disabled={!vm.matchSearchDistanceState.valid}>
              <Icon path={mdiMagnify} title="" color="white" size={1} className="text-primary" />
            </button>
          </div>
          <ModalBody>
            <div className="terrace-modal-content">
              {vm.matchedVertices.length > 0 && (
                <table className="usa-table usa-table--borderless mv-table">
                  <thead>
                    <tr>
                      <th className="narrow-td">Select</th>
                      <th className="narrow-td">Channel Name</th>
                      <th className="narrow-td">Channel Type</th>
                      <th className="narrow-td">Station</th>
                      <th className="narrow-td">Elevation</th>
                      <th className="narrow-td">Distance (ft)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {vm.matchedVertices.map((rowData, index) => (
                      <tr key={`row-${index}`}>
                        <td>
                          <div className="usa-radio">
                            <input
                              id={`radio-${index}`}
                              className="usa-radio__input"
                              type="radio"
                              name="vertex-select"
                              checked={vm.selectedVertexIndex === index}
                              onChange={() => {
                                vm.handleVertexSelect(index);
                              }}
                            />
                            <label className="usa-radio__label" htmlFor={`radio-${index}`}></label>
                          </div>
                        </td>
                        <td className="text-align-left">{rowData.channelName}</td>
                        <td className="text-align-left">{structureTypes[rowData.designElementTypeId]}</td>
                        <td>{rowData.matchedReachStationName}</td>
                        <td>{rowData.channelElevation?.toFixed(2)}</td>
                        <td>{rowData.distance?.toFixed(1)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </ModalBody>
          <ModalFooter>
            <div className='modal-buttons'>
              <button className="usa-button usa-button--outline" aria-label="Cancel" onClick={vm.closeMatchVertexModal}>
                Close
              </button>
              <button className="usa-button" aria-label="Apply" onClick={vm.applyMatchedVertex} disabled={vm.selectedVertexIndex === undefined}>
                Apply
              </button>
            </div>
          </ModalFooter>
        </div>
      </Draggable>
    </Modal>
  );
});